'use client';
import React, { useEffect, useState } from 'react';
import Hooks from '@benzinga/hooks';
import styled from '@benzinga/themetron';
import AnimatedNumber from 'animated-number-react';
import TransparentlyResult from './TransparentlyResult';
import CurrencyInputWrapper from './CurrencyInputWrapper';
import { MortgageChart } from './MortgageChart';
import { NoFirstRender } from '@benzinga/hooks';
import { SessionContext } from '@benzinga/session-context';
import { GeoManager } from '@benzinga/geo-manager';
import { Switch } from 'antd';

export type MortgageLoanType = 'purchase' | 'refinance';

export interface MortgageCalculatorProps {
  pid?: number;
  type?: MortgageLoanType;
}

interface MortgageData {
  apr: number;
  query: {
    creditScoreBucket: string;
    loanAmountBucket: string;
    loanToValueBucket: string;
    loanType: MortgageLoanType;
    program: string;
    stateAbbreviation: string;
  };
  rate: number;
  volume: number;
}

const StyledButton = styled.button`
  background-color: #ff7b00;
  color: #fefefe;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.21);
  text-shadow: 1px 0 0 rgba(0, 0, 0, 0.1);
  transition: all 0.15s ease-in-out;
  font-family: sans-serif;
  letter-spacing: 0.09em;
  min-width: 120px;
  &:hover {
    transform: translateY(-1px);
    box-shadow:
      0 2px 1px rgba(50, 50, 93, 0.2),
      0 3px 6px rgba(0, 0, 0, 0.1);
  }
`;

const inputStyles = {
  backgroundColor: ' #fefefe',
  border: '1px solid #ccc',
  color: '#0a0a0a',
} as React.CSSProperties;

export const MortgageCalculator: React.FC<MortgageCalculatorProps> = ({ pid = '', type }) => {
  const [loanType, setLoanType] = useState(type ?? 'purchase');
  const [loanProgram, setLoanProgram] = useState('Fixed30Year');
  const [loanProgramLabel, setLoanProgramLabel] = useState('30 Year Fixed');
  const [term, setTerm] = useState<number>(30);
  const [update, setUpdate] = useState<boolean>(false);
  const [esimatedMonthlyPayment, setEsimatedMonthlyPayment] = useState<number>(0);
  const [purchasePrice, setPurchasePrice] = useState<number>(220000);
  const [downPayment, setDownPayment] = useState<number>(25000);
  const [mortgageBalance, setMortgageBalance] = useState<number>(150000);
  const [homeValue, setHomeValue] = useState<number>(250000);

  const [basePandIPayment, setBasePandIPayment] = useState<number>(0); //excluding all other items (tax, insurance,pmi)

  const [propertyTax, setPropertyTax] = useState<number>(1.8);
  const [monthlyPropertyTax, setMonthlyPropertyTax] = useState<number>(330);
  const PMIPerctange = 0.58;
  const [homeInsurance, setHomeInsurance] = useState<number>(1260); //yearly
  const [includePMI, setIncludePMI] = useState<boolean>(true);
  const calculatePMI = React.useCallback(
    (purchasePrice, PMIPerctange, downPayment) => {
      if (includePMI) {
        if ((100 * downPayment) / purchasePrice > 20) {
          return 0;
        }
        return (PMIPerctange * purchasePrice) / 100 / 12;
      }
      return 0;
    },
    [includePMI],
  );
  const [monthlyPMI, setMonthlyPMI] = useState<number>(calculatePMI(purchasePrice, PMIPerctange, downPayment));
  const [HOAFees, setHOAFees] = useState<number>(0);

  const [zipCode, setZipCode] = useState('');
  const [apr, setApr] = useState<number>(7.1);
  const [rate, setRate] = useState<number>(6.513);
  const [invalidRateError, setInvalidRateError] = useState<string>('');
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [manualRate, setManualRate] = useState<boolean>(false);
  // const [propertyUse, setPropertyUse] = useState('1');
  // const [propertyType, setPropertyType] = useState('1');
  // const [creditScore, setCreditScore] = useState('3');
  // const [militaryService, setMilitaryService] = useState('2');
  const [wrongPurchasePriceError, setWrongPurchasePriceError] = useState('');
  const [lenders] = useState([]);
  const session = React.useContext(SessionContext);
  const geoManager = session.getManager(GeoManager);

  const calculateLoanAmount = React.useCallback(() => {
    if (loanType === 'purchase') return purchasePrice - downPayment;
    return mortgageBalance;
  }, [downPayment, mortgageBalance, purchasePrice, loanType]);

  const calculatePropertyValue = React.useCallback(() => {
    if (loanType === 'purchase') return purchasePrice;
    return homeValue;
  }, [homeValue, purchasePrice, loanType]);

  const calculateMonthlyPropertyTax = React.useCallback((purchasePrice, propertyTax) => {
    return (propertyTax * purchasePrice) / 100 / 12;
  }, []);

  const updateEstimatedPayment = React.useCallback(() => {
    const interest = rate / 100 / 12;
    const updatedTerm = term * 12;
    const principal = calculateLoanAmount();
    const result = principal * ((interest * (1 + interest) ** updatedTerm) / ((1 + interest) ** updatedTerm - 1));

    const monthlyTax = calculateMonthlyPropertyTax(purchasePrice, propertyTax);
    setMonthlyPropertyTax(monthlyTax);

    const pmiAddon = includePMI ? calculatePMI(purchasePrice, PMIPerctange, downPayment) : 0;
    setMonthlyPMI(pmiAddon);

    setBasePandIPayment(result);

    setEsimatedMonthlyPayment(result + monthlyTax + homeInsurance / 12 + pmiAddon + HOAFees);
  }, [
    calculateLoanAmount,
    rate,
    term,
    propertyTax,
    purchasePrice,
    homeInsurance,
    downPayment,
    HOAFees,
    calculatePMI,
    calculateMonthlyPropertyTax,
    includePMI,
  ]);

  const getGeoZip = async () => {
    const zipCode = await geoManager.getZipCode();
    if (zipCode.ok) {
      setZipCode(zipCode.ok);
    } else {
      console.error(`Error For Getting ZipCode`);
    }
  };

  useEffect(() => {
    updateEstimatedPayment();
  }, [updateEstimatedPayment]);

  const getCurrentRates = React.useCallback(() => {
    if (!manualRate) {
      const propertyValue = calculatePropertyValue();
      const loanAmount = calculateLoanAmount();
      fetch(
        `https://www.benzinga.com/lavapress/api/mortgages/current-rates?zip_code=${zipCode}&property_value=${propertyValue}&loan_amount=${loanAmount}&type=${loanType}&program=${loanProgram}`,
      )
        .then((response: Response) => response.json())
        .then((data: MortgageData) => {
          data?.apr && data.apr != 0 ? setApr(data.apr) : setApr(7.1);
          data?.rate && data.rate != 0 ? setRate(data.rate) : setRate(5.778);
          updateEstimatedPayment();
        });
    }
  }, [calculateLoanAmount, calculatePropertyValue, loanType, loanProgram, updateEstimatedPayment, zipCode, manualRate]);

  const goToFunnel = () => {
    window.open(
      `/mortgages/find?zip_code=${zipCode}&type=${loanType}&pid=${pid}&referer=${window.location.pathname}`,
      '_blank',
    );
  };

  const toggleAdvancedCalculator = () => {
    setShowAdvanced(!showAdvanced);
  };

  useEffect(() => {
    if (update) {
      getCurrentRates();
      setUpdate(false);
    }
  }, [
    purchasePrice,
    downPayment,
    homeValue,
    mortgageBalance,
    zipCode,
    getCurrentRates,
    update,
    propertyTax,
    homeInsurance,
    includePMI,
    HOAFees,
    rate,
    manualRate,
  ]);

  const onChangePurchasePrice = React.useCallback(
    (floatvalue: string) => {
      if (validatePurchasePrices(Number(floatvalue), downPayment)) {
        setUpdate(true);
      }
      setPurchasePrice(Number(floatvalue));
    },
    [downPayment],
  );

  const validatePurchasePrices = (currentPurchasePrice, currentDownPaymentPrice) => {
    if (currentPurchasePrice > currentDownPaymentPrice) {
      setWrongPurchasePriceError('');
      return true;
    } else {
      setWrongPurchasePriceError('Purchase Price should be grater than Downpayment');
      return false;
    }
  };

  const onChangeDownPayment = React.useCallback(
    (floatvalue: string) => {
      if (validatePurchasePrices(purchasePrice, Number(floatvalue))) {
        setUpdate(true);
      }
      setDownPayment(Number(floatvalue));
    },
    [purchasePrice],
  );

  const onChangeMortgageBalance = React.useCallback((floatvalue: string) => {
    setMortgageBalance(Number(floatvalue));
    setUpdate(true);
  }, []);

  const onChangeHomeValue = React.useCallback((floatvalue: string) => {
    setHomeValue(Number(floatvalue));
    setUpdate(true);
  }, []);

  const onChangeZipCode = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setZipCode(e.target.value);
    setUpdate(true);
  }, []);

  const onChangePropertyTax = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPropertyTax(Number(e.target.value));
    setUpdate(true);
  }, []);

  const onChangeHomeInsurance = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setHomeInsurance(Number(e.target.value));
    setUpdate(true);
  }, []);

  const onChangeHandlePMI = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIncludePMI(!!e.target.checked);
    setUpdate(true);
  }, []);

  const onChangeHOAFees = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setHOAFees(Number(e.target.value));
    setUpdate(true);
  }, []);

  const onChangeRate = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (manualRate) {
        const userInputedRate = Number(e.target.value);
        if (userInputedRate > 0 && userInputedRate < 100) {
          setRate(Number(e.target.value));
          setInvalidRateError('');
        } else {
          setInvalidRateError('Invalid Rate. Rate Should be between 0 and 100');
        }
      }
    },
    [manualRate],
  );

  const onChangeHandleManualMortgageRate = React.useCallback((status: boolean) => {
    setManualRate(status);
    setUpdate(true);
  }, []);

  const tabStyles = (tabType: string) => {
    const initialStyles = { paddingBottom: '4px', width: '50%' };
    if (loanType === tabType) {
      return { borderBottom: '5px solid rgb(24, 121, 255)', ...initialStyles };
    }
    return { borderBottom: '5px solid #c4c4c4', ...initialStyles };
  };

  const onChangeLoanProgram = React.useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const loanP = e.target.value;

    switch (loanP) {
      case 'Fixed30Year':
        setTerm(30);
        setLoanProgramLabel('30 Year Fixed');
        break;

      case 'Fixed15Year':
        setTerm(15);
        setLoanProgramLabel('15 Years Fixed');
        break;

      case 'ARM7':
        setTerm(7);
        setLoanProgramLabel('7/1 ARM (adjustable rate)');
        break;

      case 'ARM5':
        setTerm(5);
        setLoanProgramLabel('5/1 ARM (adjustable rate)');
        break;

      default:
        setTerm(30);
        setLoanProgramLabel('30 Year Fixed');
        break;
    }

    setLoanProgram(e.target.value);
    setUpdate(true);
  }, []);

  Hooks.useEffectDidMount(() => {
    getGeoZip();
  });

  return (
    <MortgageCalculatorWrapper className="mortgage-calculator-wrapper bg-white box-border flex flex-col h-auto w-full justify-center p-4 py-8 relative wrap-block flex-col bg-white border border-t-4 shadow-sm">
      <div className="mortgage-calculator" id="purchase-calculator">
        <div className="mortgage-calculator-inputs">
          <div className="flex space-x-6">
            <button
              onClick={() => {
                setLoanType('purchase');
                setUpdate(true);
              }}
              style={tabStyles('purchase')}
              type="button"
            >
              Purchase
            </button>
            <button
              onClick={() => {
                setLoanType('refinance');
                setUpdate(true);
              }}
              style={tabStyles('refinance')}
              type="button"
            >
              Refinance
            </button>
          </div>
          <div className="mortgage-purchase-inputs">
            <div className="mt-4">
              {loanType === 'purchase' ? (
                <>
                  <CurrencyInputWrapper
                    inputName="purchase_price"
                    labelText="What's the purchase price of the home?"
                    onChange={(floatvalue: string) => onChangePurchasePrice(floatvalue)}
                    value={purchasePrice}
                  />
                  {wrongPurchasePriceError && <span className="error">{wrongPurchasePriceError}</span>}
                  <CurrencyInputWrapper
                    inputName="down_payment"
                    labelText="How much do you want to put down?"
                    onChange={(floatvalue: string) => onChangeDownPayment(floatvalue)}
                    value={downPayment}
                  />
                </>
              ) : (
                <>
                  <CurrencyInputWrapper
                    inputName="mortgage_balance"
                    labelText="What is your current mortgage balance?"
                    onChange={(floatvalue: string) => onChangeMortgageBalance(floatvalue)}
                    value={mortgageBalance}
                  />
                  <CurrencyInputWrapper
                    inputName="home_value"
                    labelText="Approximately what is your home worth?"
                    onChange={(floatvalue: string) => onChangeHomeValue(floatvalue)}
                    value={homeValue}
                  />
                </>
              )}
              <label className="w-full text-left text-xs font-semibold" htmlFor="zip_code">
                Enter Zip Code
              </label>
              <input
                className="appearance-none relative w-full h-10 box-border  overflow-visible p-2 text-base rounded"
                id="zip_code"
                name="zip_code"
                onChange={onChangeZipCode}
                style={inputStyles}
                value={zipCode}
              />
              <label className="w-full text-left text-xs font-semibold" htmlFor="loan_program">
                Select Loan Period
              </label>
              <select
                className="relative w-full h-10 box-border block overflow-visible p-2 mb-2 text-base rounded"
                id="loan_program"
                name="loan_program"
                onChange={onChangeLoanProgram}
                style={inputStyles}
              >
                <option value="Fixed30Year">30-year fixed</option>
                <option value="Fixed15Year">15-year fixed</option>
                <option value="ARM7">7/1 ARM (adjustable rate)</option>
                <option value="ARM5">5/1 ARM (adjustable rate)</option>
              </select>

              <div className="mt-2">
                <label className="w-full text-left text-xs font-semibold pr-2" htmlFor="manual_mortgage_rate">
                  Manual Mortgage Rate?
                </label>
                <Switch id={'manual_mortgage_rate'} onChange={onChangeHandleManualMortgageRate} />
              </div>
              {manualRate && (
                <>
                  <label className="w-full text-left text-xs font-semibold" htmlFor="mortgage_rate">
                    Enter Mortgage Rate
                  </label>
                  <input
                    className="appearance-none relative w-full h-10 box-border overflow-visible p-2 mb-2 text-base rounded"
                    id="mortgage_rate"
                    max="10"
                    min="1"
                    name="mortgage_rate"
                    onChange={onChangeRate}
                    style={inputStyles}
                    type="number"
                  />
                </>
              )}
              {invalidRateError && <span className="error">{invalidRateError}</span>}
            </div>

            <div>
              <label className="w-full text-left text-xs font-semibold pr-2" htmlFor="advanced_calc">
                Advanced Mode
              </label>
              <Switch id={'advanced_calc'} onChange={toggleAdvancedCalculator} />

              {showAdvanced && (
                <div className="advanced-calculator grid-x">
                  <div>
                    <label className="w-full text-left text-xs font-semibold" htmlFor="property_tax">
                      Enter Property Tax(%)
                    </label>
                    <input
                      className="appearance-none relative w-full h-10 box-border block overflow-visible p-2 mb-2 text-base rounded"
                      id="property_tax"
                      name="property_tax"
                      onChange={onChangePropertyTax}
                      style={inputStyles}
                      type="number"
                      value={propertyTax}
                    />
                  </div>
                  <div>
                    <label className="w-full text-left text-xs font-semibold" htmlFor="home_insurance">
                      Home insurance (per year)
                    </label>
                    <input
                      className="appearance-none relative w-full h-10 box-border block overflow-visible p-2 mb-2 text-base rounded"
                      id="home_insurance"
                      name="home_insurance"
                      onChange={onChangeHomeInsurance}
                      style={inputStyles}
                      type="number"
                      value={homeInsurance}
                    />
                  </div>
                  <div className="my-2">
                    <label className="w-full text-left text-xs font-semibold pr-2" htmlFor="include_pmi">
                      Include PMI?
                    </label>
                    <input
                      checked={includePMI}
                      className="btn-radio"
                      name="include_pmi"
                      onChange={onChangeHandlePMI}
                      type="checkbox"
                      value="1"
                    />
                  </div>
                  <div>
                    <label className="w-full text-left text-xs font-semibold" htmlFor="hoa_fees">
                      HOA Fee (per month)
                    </label>
                    <input
                      className="appearance-none relative w-full h-10 box-border block overflow-visible p-2 mb-2 text-base rounded"
                      id="hoa_fees"
                      name="hoa_fees"
                      onChange={onChangeHOAFees}
                      style={inputStyles}
                      type="number"
                      value={HOAFees}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mortgage-calculator-results">
          <label className="text-sm font-semibold">Estimated Monthly Payment</label>
          <h2 className="font-bold text-4xl">
            <NoFirstRender>
              <AnimatedNumber
                formatValue={(value: number) => (value ? `$${value.toFixed(2)}` : '–')}
                value={esimatedMonthlyPayment}
              />
            </NoFirstRender>
          </h2>
          <h4 className="pt-4">Monthly Payment Breakdown</h4>
          <table className="w-full table-auto mb-4">
            <tr>
              <td>
                <label className="text-xs font-semibold">Principal & Interest</label>
              </td>
              <td className="text-right">
                <h5 className="font-bold text-1xl">
                  <NoFirstRender>
                    <AnimatedNumber
                      formatValue={(value: number) => (value ? `$${value.toFixed(2)}` : '–')}
                      value={basePandIPayment}
                    />
                  </NoFirstRender>
                </h5>
              </td>
            </tr>
            <tr>
              <td>
                <label className="text-xs font-semibold">Monthly Property Tax</label>
              </td>
              <td className="text-right">
                <h5 className="font-bold text-1xl">
                  <NoFirstRender>
                    <AnimatedNumber
                      formatValue={(value: number) => (value ? `$${value.toFixed(2)}` : '–')}
                      value={monthlyPropertyTax}
                    />
                  </NoFirstRender>
                </h5>
              </td>
            </tr>
            <tr>
              <td>
                <label className="text-xs font-semibold">Home Insurance</label>
              </td>
              <td className="text-right">
                <h5 className="font-bold text-1xl">
                  <NoFirstRender>
                    <AnimatedNumber
                      formatValue={(value: number) => (value ? `$${value.toFixed(2)}` : '–')}
                      value={homeInsurance / 12}
                    />
                  </NoFirstRender>
                </h5>
              </td>
            </tr>
            {includePMI && (
              <tr>
                <td>
                  <label className="text-xs font-semibold">PMI</label>
                </td>
                <td className="text-right">
                  <h5 className="font-bold text-1xl">
                    <NoFirstRender>
                      <AnimatedNumber
                        formatValue={(value: number) => (value ? `$${value.toFixed(2)}` : '–')}
                        value={monthlyPMI}
                      />
                    </NoFirstRender>
                  </h5>
                </td>
              </tr>
            )}

            {HOAFees > 0 && (
              <tr>
                <td>
                  <label className="text-xs font-semibold">HOA</label>
                </td>
                <td className="text-right">
                  <h5 className="font-bold text-1xl">
                    <NoFirstRender>
                      <AnimatedNumber
                        formatValue={(value: number) => (value ? `$${value.toFixed(2)}` : '–')}
                        value={HOAFees}
                      />
                    </NoFirstRender>
                  </h5>
                </td>
              </tr>
            )}
          </table>

          <div className="interset-rate flex items-center justify-between mb-1">
            <label className="text-xs font-semibold">Rate/APR</label>
            <h2 className="font-semibold text-2xl">
              {rate ? `${rate}%` : '–'} / {apr ? `${apr}%` : '–'}
            </h2>
          </div>
          <div className="text-xs mb-2 right">
            See more mortgage rates on{' '}
            <a className="text-blue-500" href="https://www.zillow.com/" rel="noreferrer nooppener" target="_blank">
              Zillow
            </a>
          </div>
          <div className="clear-both fixed-term flex items-center justify-between mb-4">
            <label className="text-xs font-semibold">Mortgage Type</label>
            <h2 className="font-semibold text-2xl">{loanProgramLabel}</h2>
          </div>
          <StyledButton
            className="appearance-none antialiased rounded text-center w-full h-10 mt-1 font-semibold uppercase text-sm flex items-center justify-center translate-x-0"
            onClick={goToFunnel}
            type="button"
          >
            Find Lenders
          </StyledButton>
        </div>
      </div>

      {esimatedMonthlyPayment > 0 && (
        <MortgageChart
          esimatedMonthlyPayment={basePandIPayment}
          interestRate={rate}
          loanProgram={loanProgram}
          totalPrincipal={loanType == 'purchase' ? purchasePrice - downPayment : mortgageBalance}
        />
      )}
      {lenders.length ? (
        <div className="lenders">
          {lenders.map((lender, i) => {
            const key = `result-${i}`;
            return <TransparentlyResult key={key} result={lender} />;
          })}
        </div>
      ) : (
        ''
      )}
    </MortgageCalculatorWrapper>
  );
};

const MortgageCalculatorWrapper = styled.div`
  border-top-color: rgb(255, 90, 31);
  .mortgage-calculator {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
    .mortgage-calculator-inputs,
    .mortgage-calculator-results {
      grid-column: span 1 / span 1;
    }
    .error {
      color: red;
      display: block;
      font-size: 0.8rem;
    }
    @media (max-width: 800px) {
      .mortgage-calculator-inputs,
      .mortgage-calculator-results {
        grid-column: span 2 / span 2;
      }
    }
  }
`;
